import { ShopProductStatus } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { Badge, cn } from '@utima/ui';
import { Package } from 'lucide-react';
import { memo, useMemo } from 'react';

import { StatsSummaryShell } from '@/components/statsSummary/StatsSummaryShell';
import { StatSummaryRow } from '@/components/statsSummary/StatSummaryRow';
import { useEnumTranslations } from '@/hooks';

export type ProductStatsSummaryProps = {
  foundCount?: number;
  awaitingImportCount?: number;
  pendingCount: number;
  embeddingCount: number;
  doneCount: number;
  failedCount: number;
  activeStatusFilter?: ShopProductStatus;
  onStatusFilterChange?: (status: ShopProductStatus | undefined) => void;
};

/**
 * KB-style processing-status bar for the product table, composed from the
 * shared shell extracted from `KBStatsSummary` — see
 * `docs/implementation/2026-07-18-product-table-ux-and-feed-settings-modal/plan.md`
 * (Phase 2, D1/D6). Single-select: clicking the active row's status again
 * clears the filter.
 */
export const ProductStatsSummary = memo(function ProductStatsSummary({
  foundCount,
  awaitingImportCount = 0,
  pendingCount,
  embeddingCount,
  doneCount,
  failedCount,
  activeStatusFilter,
  onStatusFilterChange,
}: ProductStatsSummaryProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();

  const total = pendingCount + embeddingCount + doneCount + failedCount;

  const completePercent = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  const isFullyComplete = total > 0 && doneCount === total && failedCount === 0;

  const isInteractive = !!onStatusFilterChange;

  const handleToggle = (status: ShopProductStatus) =>
    onStatusFilterChange?.(activeStatusFilter === status ? undefined : status);

  const doneFilterActive = activeStatusFilter === ShopProductStatus.Done;
  const failedFilterActive = activeStatusFilter === ShopProductStatus.Failed;

  const statRows = useMemo(
    () => [
      {
        id: ShopProductStatus.Done,
        title: enumTranslations.shopProductStatus[ShopProductStatus.Done],
        count: doneCount,
        isError: false,
      },
      {
        id: ShopProductStatus.Embedding,
        title: enumTranslations.shopProductStatus[ShopProductStatus.Embedding],
        count: embeddingCount,
        isError: false,
      },
      {
        id: ShopProductStatus.Pending,
        title: enumTranslations.shopProductStatus[ShopProductStatus.Pending],
        count: pendingCount,
        isError: false,
      },
      {
        id: ShopProductStatus.Failed,
        title: enumTranslations.shopProductStatus[ShopProductStatus.Failed],
        count: failedCount,
        isError: true,
      },
    ],
    [enumTranslations, doneCount, embeddingCount, pendingCount, failedCount],
  );

  const summary = (
    <>
      {foundCount !== undefined && (
        <span className='text-xs tabular-nums text-muted-fg'>
          {t`Found`}: <strong>{foundCount}</strong>
        </span>
      )}
      <button
        type='button'
        disabled={!isInteractive}
        className={cn(
          'text-sm tabular-nums',
          'rounded px-1',
          isInteractive && 'cursor-pointer hover:bg-muted/50',
          doneFilterActive && 'bg-accent font-medium ring-1 ring-primary',
        )}
        onClick={
          isInteractive ? () => handleToggle(ShopProductStatus.Done) : undefined
        }
      >
        <span>{t`Done`}</span>{' '}
        <strong>
          {doneCount}/{total}
        </strong>
      </button>

      {failedCount > 0 && (
        <Badge
          variant='danger'
          size='sm'
          className={cn(
            isInteractive && 'cursor-pointer',
            failedFilterActive && 'ring-2 ring-danger',
          )}
          onClick={
            isInteractive
              ? () => handleToggle(ShopProductStatus.Failed)
              : undefined
          }
        >
          {failedCount} {t`failed`}
        </Badge>
      )}

      {pendingCount > 0 && (
        <span className='text-xs text-muted-fg'>
          {t`Pending`}: {pendingCount}
        </span>
      )}
      {embeddingCount > 0 && (
        <span className='text-xs text-muted-fg'>
          {enumTranslations.shopProductStatus[ShopProductStatus.Embedding]}:{' '}
          {embeddingCount}
        </span>
      )}
    </>
  );

  return (
    <StatsSummaryShell
      icon={Package}
      label={t`Product status`}
      progressValue={doneCount}
      progressMax={total > 0 ? total : 100}
      completePercent={completePercent}
      isFullyComplete={isFullyComplete}
      summary={summary}
    >
      <section>
        <p className='mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-fg'>
          {t`Readiness`}
        </p>
        <div className='flex flex-col'>
          {foundCount !== undefined && (
            <StatSummaryRow title={t`Found`} count={foundCount} isTotal />
          )}
          {awaitingImportCount > 0 && (
            <StatSummaryRow
              title={t`Awaiting import`}
              count={awaitingImportCount}
            />
          )}

          {statRows.map(stat => {
            const isInactive = stat.count === 0;
            const isActive = !isInactive && activeStatusFilter === stat.id;

            return (
              <StatSummaryRow
                key={stat.id}
                title={stat.title}
                count={stat.count}
                highlight={isActive ? 'solid' : 'none'}
                isError={stat.isError}
                isInactive={isInactive}
                onClick={
                  isInactive || !isInteractive
                    ? undefined
                    : () => handleToggle(stat.id)
                }
              />
            );
          })}
        </div>
      </section>
    </StatsSummaryShell>
  );
});
